import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useState } from "react";
import Modal from "@src/common/Modal";
import { MAX_OTP_TIMER, OTP_INDEXES } from "@src/config/constant";

interface RegisterOtpPopupProps {
    show: boolean;
    handleClose: () => void;
    email?: string;
    onVerify: (otp: string) => void;
    onResend?: () => void;
    verifying?: boolean;
}

const RegisterOtpPopup = ({
    show,
    handleClose,
    email = "",
    onVerify,
    onResend,
    verifying = false,
}: RegisterOtpPopupProps) => {
    const [otp, setOtp] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [otpTimer, setOtpTimer] = useState(MAX_OTP_TIMER);

    useEffect(() => {
        if (!show) {
            return undefined;
        }

        setOtp("");
        setErrorMessage("");
        setOtpTimer(MAX_OTP_TIMER);
        document.body.classList.add("overflow-hidden");

        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, [show]);

    useEffect(() => {
        if (!show || otpTimer < 0) {
            return undefined;
        }

        const timerId = setInterval(() => {
            setOtpTimer((currentTimer) => currentTimer - 1);
        }, 1000);

        return () => clearInterval(timerId);
    }, [otpTimer, show]);

    const updateOtpAtIndex = (otpValue: string, index: number, nextCharacter: string) => {
        const otpCharacters = OTP_INDEXES.map((currentIndex) => otpValue[currentIndex] || "");
        otpCharacters[index] = nextCharacter;
        return otpCharacters.join("").slice(0, OTP_INDEXES.length);
    };

    const handleOtpChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        const nextCharacter = event.target.value.replace(/\D/g, "").slice(-1);
        const nextOtp = updateOtpAtIndex(otp, index, nextCharacter);

        setOtp(nextOtp);
        setErrorMessage("");

        if (nextCharacter && index < OTP_INDEXES.length - 1) {
            const nextInput = document.getElementById(`register-otp-${index + 1}`);
            nextInput?.focus();
        }
    };

    const handleOtpKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
        if (event.key !== "Backspace") {
            return;
        }

        if (otp[index]) {
            setOtp(updateOtpAtIndex(otp, index, ""));
            return;
        }

        if (index > 0) {
            const previousIndex = index - 1;
            setOtp(updateOtpAtIndex(otp, previousIndex, ""));
            const previousInput = document.getElementById(`register-otp-${previousIndex}`);
            previousInput?.focus();
        }
    };

    const handleOtpPaste = (event: ClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();

        const pastedOtp = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_INDEXES.length);

        if (!pastedOtp) {
            return;
        }

        setOtp(pastedOtp);
        setErrorMessage("");

        const focusIndex = Math.min(pastedOtp.length, OTP_INDEXES.length) - 1;
        if (focusIndex >= 0) {
            const nextInput = document.getElementById(`register-otp-${focusIndex}`);
            nextInput?.focus();
        }
    };

    const handleVerify = () => {
        if (otp.length !== OTP_INDEXES.length) {
            setErrorMessage("OTP is required");
            return;
        }

        onVerify(otp);
    };

    const handleResend = () => {
        if (otpTimer >= 0) {
            return;
        }

        onResend?.();
        setOtp("");
        setErrorMessage("");
        setOtpTimer(MAX_OTP_TIMER);
    };

    return (
        <div className="model-outer upload-error-popup register-otp-popup">
            <Modal show={show} handleClose={handleClose}>
                <div className="modal-header">
                    <h2>Verify OTP</h2>
                    <span>Enter the OTP sent to your registered email ID</span>
                    {/* {email ? <small className="otp-email">{email}</small> : null} */}
                </div>
                <div className="modal-body">
                    

                    <fieldset className="change-password-otp">
                        <div className="material" onPaste={handleOtpPaste}>
                            <div className="otp-box-wrapper">
                                {OTP_INDEXES.map((otpIndex) => (
                                    <input
                                        key={otpIndex}
                                        id={`register-otp-${otpIndex}`}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        className="otp-box"
                                        value={otp[otpIndex] || ""}
                                        onChange={(event) => handleOtpChange(event, otpIndex)}
                                        onKeyDown={(event) => handleOtpKeyDown(event, otpIndex)}
                                    />
                                ))}
                            </div>

                            {errorMessage ? <span className="error-txt">{errorMessage}</span> : null}
                        </div>

                        <span
                            onClick={handleResend}
                            className={`resend-otp-txt ${otpTimer < 0 ? "resend-otp" : ""}`}
                        >
                            {otpTimer < 0 ? "Resend OTP" : `Resend in 0:${otpTimer > 9 ? otpTimer : `0${otpTimer}`}`}
                        </span>
                    </fieldset>
                </div>

                <div className="d-flex" style={{ gap: "12px", marginTop: "24px" }}>
                    <button type="button" className="btn-primary w-100" onClick={handleVerify} disabled={verifying}>
                        Verify
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default RegisterOtpPopup;
